import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useEffect, cloneElement } from 'react';
import clsx from 'clsx';
import { BasePositioner } from '../../BasePositioner/BasePositioner/index.js';
import { Placements, TooltipTriggers } from '../types/index.js';
import { ArrowIcon } from '../ArrowIcon/index.js';
import { useStyles } from '../styles/index.js';
import { mapPlacement, getArrowStyle } from '../placement/index.js';
import { useClickOutsideRef } from '../../../hooks/src/useClickOutsideRef/index.js';

function Tooltip({ target, hasTestIds, placement = Placements.Top, trigger = TooltipTriggers.Hover, children, dataTestId, zIndex = 1, className, rootClassName, preventOutsideClick, isOpen, notClickable, hideArrow = false, container, contentClassName, }) {
    const [show, setShow] = useState(false);
    const [currentPlacement, setCurrentPlacement] = useState(placement);
    const [referenceWidth, setReferenceWidth] = useState(0);
    const tooltipContentRef = useRef(null);
    const ownStyles = useStyles({ zIndex, notClickable, placement });
    const toggle = () => {
        setShow(prev => !prev);
    };
    const isControlled = isOpen !== undefined;
    useEffect(() => {
        if (isControlled) {
            setShow(isOpen);
        }
    }, [isOpen, isControlled]);
    useEffect(() => {
        setCurrentPlacement(placement);
    }, [placement]);
    const popperContentRef = useClickOutsideRef(() => !preventOutsideClick || !isOpen && setShow(false));
    const getContent = () => (jsxs("div", { ref: tooltipContentRef, className: clsx(ownStyles.root, className), "data-testid": "tooltip-content", style: { position: 'relative' }, children: [!hideArrow && (jsx("div", { className: ownStyles.indicatorWrapper, "data-arrow-wrapper-dir": currentPlacement, style: getArrowStyle(currentPlacement, referenceWidth), children: jsx(ArrowIcon, { className: ownStyles.indicator, "data-arrow-dir": currentPlacement }) })), jsx("div", { className: clsx(ownStyles.content, contentClassName), children: children })] }));
    const open = () => {
        if (!isControlled) {
            setShow(true);
        }
    };
    const close = () => {
        if (!isControlled) {
            setShow(false);
        }
    };
    const handlePositionChange = (position) => {
        const newPlacement = Object.values(Placements).find(p => mapPlacement(p) === position.placement);
        if (newPlacement && newPlacement !== currentPlacement) {
            setCurrentPlacement(newPlacement);
        }
        if (position.referenceWidth !== undefined) {
            setReferenceWidth(position.referenceWidth);
        }
    };
    return (jsx("div", { ref: popperContentRef, "data-testid": dataTestId || 'tooltip-root', className: rootClassName, children: jsx(BasePositioner, { reference: cloneElement(target, {
                'data-popper-target': true,
                ...(!isControlled && { onMouseEnter: trigger === TooltipTriggers.Hover ? open : null }),
                ...(!isControlled && {
                    onClick: (e) => {
                        target.props.onClick?.(e);
                        trigger === TooltipTriggers.Click && toggle();
                    },
                }),
                ...(!isControlled && { onMouseLeave: trigger === TooltipTriggers.Hover ? close : null }),
                ...(!hasTestIds && { 'data-testid': 'tooltip-target' }),
            }), content: getContent(), isOpen: show, onClose: close, placement: mapPlacement(placement), zIndex: zIndex, offset: placement.includes('top') || placement.includes('bottom') || placement.includes('auto') ? [10, 0] : [0, 10], onPositionChange: handlePositionChange, portalContainer: container }) }));
}
Tooltip.displayName = 'Tooltip';

export { Tooltip };
